import type { ComponentProps } from "react";
import { useTranslation } from "react-i18next";
import type { ForgotPasswordForm, SigninForm, SignupForm } from "@lyder-talk/ui";

/**
 * Translated `copy` for the shared auth forms in `@lyder-talk/ui`.
 *
 * The forms take their strings as props on purpose — see the note at the top of
 * `auth-copy.ts` in the package: the admin panel renders the same components and
 * must not inherit a learner locale. That leaves every *caller* restating ~20
 * keys, and the three `/auth/*` pages plus the required-auth modal were four
 * copies of the same block, drifting apart one string at a time.
 *
 * These hooks hold that block once. A new caller gets the app's language for
 * free and cannot drift.
 */

type SigninCopy = NonNullable<ComponentProps<typeof SigninForm>["copy"]>;
type SignupCopy = NonNullable<ComponentProps<typeof SignupForm>["copy"]>;
type ForgotCopy = NonNullable<ComponentProps<typeof ForgotPasswordForm>["copy"]>;

/** The legal footnote, shared by the sign-in and sign-up forms. */
export const useAuthLegalCopy = () => {
  const { t } = useTranslation();

  return {
    termsPrefix: t("auth.forms.legal.termsPrefix", "By continuing you agree to our"),
    termsLink: t("auth.forms.legal.termsLink", "Terms"),
    andPrefix: t("auth.forms.legal.andPrefix", "and"),
    privacyLink: t("auth.forms.legal.privacyLink", "Privacy Policy"),
    orContinueWith: t("auth.forms.legal.orContinueWith", "or continue with"),
  };
};

export const useSigninFormCopy = (): SigninCopy => {
  const { t } = useTranslation();
  const legal = useAuthLegalCopy();

  return {
    showPasswordLabel: t("auth.forms.signin.showPassword", "Show password"),
    hidePasswordLabel: t("auth.forms.signin.hidePassword", "Hide password"),
    title: t("auth.forms.signin.title", "Welcome back"),
    description: t("auth.forms.signin.description", "Pick up your streak where you left it."),
    emailLabel: t("auth.forms.signin.emailLabel", "Email address"),
    emailPlaceholder: t("auth.forms.signin.emailPlaceholder", "you@example.com"),
    passwordLabel: t("auth.forms.signin.passwordLabel", "Password"),
    forgotPassword: t("auth.forms.signin.forgotPassword", "Forgot password?"),
    submitting: t("auth.forms.signin.submitting", "Signing you in…"),
    submit: t("auth.forms.signin.submit", "Sign in"),
    continueWithGoogle: t("auth.forms.signin.continueWithGoogle", "Continue with Google"),
    newHere: t("auth.forms.signin.newHere", "New here?"),
    createAccount: t("auth.forms.signin.createAccount", "Create an account"),
    genericError: t("auth.forms.signin.genericError", "An error occurred. Please try again."),
    legal,
  };
};

export const useSignupFormCopy = (): SignupCopy => {
  const { t } = useTranslation();
  const legal = useAuthLegalCopy();

  return {
    showPasswordLabel: t("auth.forms.signup.showPassword", "Show password"),
    hidePasswordLabel: t("auth.forms.signup.hidePassword", "Hide password"),
    passwordStrengthLabels: [
      t("auth.passwordStrength.label", "Strength"),
      t("auth.passwordStrength.weak", "Weak"),
      t("auth.passwordStrength.fair", "Fair"),
      t("auth.passwordStrength.strong", "Strong"),
      t("auth.passwordStrength.excellent", "Excellent"),
    ],
    passwordRuleLabels: [
      t("auth.passwordRules.characters", "8+ characters"),
      t("auth.passwordRules.number", "a number"),
      t("auth.passwordRules.capital", "a capital letter"),
    ],
    successTitle: t("auth.forms.signup.successTitle", "You're all set"),
    successDescription: t(
      "auth.forms.signup.successDescription",
      "Your desk is ready. Head in and build your first topic.",
    ),
    successTitleConfirmEmail: t("auth.forms.signup.successTitleConfirmEmail", "Confirm your email"),
    successDescriptionConfirmEmail: t(
      "auth.forms.signup.successDescriptionConfirmEmail",
      "Confirm the email we just sent",
    ),
    steps: [
      t("auth.forms.signup.steps.0", "Sign in with your new account"),
      t("auth.forms.signup.steps.1", "Generate your first speaking topic"),
      t("auth.forms.signup.steps.2", "Save words you meet while you talk"),
    ],
    successActionConfirmEmail: t("auth.forms.signup.successActionConfirmEmail", "Go to sign in"),
    successAction: t("auth.forms.signup.successAction", "Start learning"),
    benefitOne: t("auth.forms.signup.benefitOne", "Save words you meet while you talk"),
    title: t("auth.forms.signup.title", "Create your account"),
    description: t(
      "auth.forms.signup.description",
      "Free to start. Your progress and saved words stay with you.",
    ),
    displayNameLabel: t("auth.forms.signup.displayNameLabel", "Display name"),
    displayNamePlaceholder: t(
      "auth.forms.signup.displayNamePlaceholder",
      "How should we greet you?",
    ),
    emailLabel: t("auth.forms.signup.emailLabel", "Email address"),
    passwordLabel: t("auth.forms.signup.passwordLabel", "Password"),
    confirmPasswordLabel: t(
      "auth.forms.signup.confirmPasswordLabel",
      "Confirm password",
    ),
    matchHint: t("auth.forms.signup.matchHint", "Both entries must match."),
    creating: t("auth.forms.signup.creating", "Creating your account…"),
    submit: t("auth.forms.signup.submit", "Create account"),
    googleLabel: t("auth.forms.signup.googleLabel", "Sign up with Google"),
    passwordTooShort: t(
      "auth.forms.signup.passwordTooShort",
      "Password must be at least 8 characters.",
    ),
    passwordsMismatch: t("auth.forms.signup.passwordsMismatch", "Passwords do not match."),
    unexpectedError: t(
      "auth.forms.signup.unexpectedError",
      "An unexpected error occurred. Please try again.",
    ),
    haveAccount: t("auth.forms.signup.haveAccount", "Already have an account?"),
    signInInstead: t("auth.forms.signup.signInInstead", "Sign in"),
    legal,
  };
};

export const useForgotPasswordFormCopy = (): ForgotCopy => {
  const { t } = useTranslation();

  return {
    title: t("auth.forms.forgot.title", "Reset your password"),
    description: t(
      "auth.forms.forgot.description",
      "Enter your email and we'll send you a link to choose a new one.",
    ),
    emailLabel: t("auth.forms.forgot.emailLabel", "Email address"),
    sending: t("auth.forms.forgot.sending", "Sending the link…"),
    submit: t("auth.forms.forgot.submit", "Send reset link"),
    backToSignIn: t("auth.forms.forgot.backToSignIn", "Back to sign in"),
    sentTitle: t("auth.forms.forgot.sentTitle", "Check your inbox"),
    sentDescription: (email) =>
      t(
        "auth.forms.forgot.sentDescription",
        "If an account exists for {{email}}, a reset link is on its way — it expires in one hour.",
        { email },
      ),
    tryAnotherEmail: t("auth.forms.forgot.tryAnotherEmail", "Try another email"),
    sendFailed: t("auth.forms.forgot.sendFailed", "Failed to send reset email. Please try again."),
  };
};
